import { MetricGroupsAndPeriods } from '../../dto/MetricGroupsAndPeriods';
import { MetricGroup } from '../../dto/MetricGroup';
import { MetricDataRole } from '../../dto/MetricData';
import { LocalDate } from '../../../../core/pojo/date-variable';
import {
  DefaultMetric,
  MetricDataType,
  MetricSign,
  MetricType,
} from '../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric';
import { DefaultMetricGroup } from "../../../recovery/component/recovery-metrics-panel/entity/DefaultMetricGroup";
import { Metric } from "../../dto/Metric";
import { MetricGroupForm, MetricGroupFormWrapper } from "../../dto/MetricGroupForm";

export class MetricCalculationService {
  /**
   * For each period and for each metric group (depending on the passed 'isActual' flag),
   * this function calculates and sets appropriate starting and ending values.
   * This is done by finding corresponding metric data and updating their 'rowValue' attribute
   * with the calculated starting value for the given period.
   * It performs these operations in provided periods excluding the first one
   * because the first starting is pre-defined.
   *
   * Note: Sets starting values only for periods that are requested.
   *
   * @param groups - Metric groups and periods structure. Period's isRequested field is ignored.
   * @param isActual - Flag determining if actual or budget data is considered.
   */
  static setStartingForAllPeriods(groups: MetricGroupsAndPeriods, isActual: boolean): void {
    const periods = isActual ? groups.periods.actual : groups.periods.budget;
    if (periods.length === 0) {
      return;
    }
    const firstPeriod = periods[0].period;
    groups.metricGroups.forEach((group) => this.calculateTotal(group, firstPeriod, isActual));

    periods
      .slice(1)
      .map((periodWrapper) => periodWrapper.period)
      .forEach((period) => {
        groups.metricGroups.forEach((group) => {
          const startingMetricData = group.metrics
            .find(({ props }) => props.type === MetricType.STARTING)?.companyMetrics[0].metricData;

          if (!startingMetricData) {
            return;
          }

          const metricData = startingMetricData.find((d) =>
            d.period === period
              && d.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
          );

          if (metricData) {
            metricData.rowValue = this.getPreviousEnding(group, period, isActual);
          }

          this.calculateTotal(group, period, isActual);
        });
      });
  }

  // does the same as the method above, but for MetricGroupFormWrapper
  static setStartingForAllPeriodsInForm(groups: MetricGroupsAndPeriods, form: MetricGroupFormWrapper, isActual: boolean): void {
    const periods = isActual ? groups.periods.actual : groups.periods.budget;
    if (periods.length === 0) {
      return;
    }

    const firstPeriod = periods[0].period;
    form.controls.groups.controls.forEach((group) => this.calculateTotalForm(group, firstPeriod, isActual));

    periods
      .slice(1)
      .map((periodWrapper) => periodWrapper.period)
      .forEach((period) => {
        form.controls.groups.controls.forEach((group) => {
          const startingMetricData = group.controls.metrics.controls
            .find((metric) => metric.controls.props.value.type === MetricType.STARTING)?.controls.companyMetrics.controls[0].controls.metricData.controls;

          if (!startingMetricData) {
            return;
          }

          const metricData = startingMetricData.find((d) =>
            d.value.period === period &&
              d.value.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
          );

          if (metricData) {
            metricData.controls.rowValue.patchValue(this.getPreviousEndingForm(group, period, isActual), { emitEvent: false });
          }

          this.calculateTotalForm(group, period, isActual);
        });
      });
  }


  /**
   * For a given metric group and period, this function calculates
   * the total for all metrics (except the one with type 'ENDING')
   * and assigns this value to the 'ENDING' metric's 'rowValue' attribute for
   * the given period. The calculated total respects the sign of each metric
   * as well as their 'isHidden' and 'role' attributes.
   *
   * @param group - Metric group
   * @param period - The period for which the total should be calculated
   * @param isActual - Whether the total needs to be calculated for actual or budget metrics
   */
  static calculateTotal(group: MetricGroup, period: LocalDate, isActual: boolean): void {
    const endingMetric = group.metrics.find((metric) => metric.props.type === MetricType.ENDING);
    if (!endingMetric) {
      return;
    }

    const metricsDifferentFromEnding = group.metrics.filter((metric) => metric.props.type !== MetricType.ENDING);

    let total = 0;
    for (const metric of metricsDifferentFromEnding) {
      if (metric.companyMetrics[0].isHidden) {
        continue;
      }
      const sign = metric.dataProps.sign === MetricSign.NEGATIVE ? -1 : 1;

      const metricData = metric.companyMetrics[0].metricData.find((data) =>
        data.period === period &&
        data.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
      );

      if (metricData) {
        total += sign * (Number(metricData.rowValue) || 0);
      }
    }

    const endingMetricData = endingMetric.companyMetrics[0].metricData.find((data) =>
      data.period === period &&
      data.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
    );


    if (endingMetricData) {
      endingMetricData.rowValue = total.toString();
    }
  }

  // does the same as the method above, but for MetricGroupForm
  static calculateTotalForm(group: MetricGroupForm, period: LocalDate, isActual: boolean): void {
    const endingMetric = group.controls.metrics.controls.find((metric) => metric.value.props.type === MetricType.ENDING);
    if (!endingMetric) {
      return;
    }

    const metricsDifferentFromEnding = group.value.metrics.filter((metric) => metric.props.type !== MetricType.ENDING);

    let total = 0;
    for (const metric of metricsDifferentFromEnding) {
      if (metric.companyMetrics[0].isHidden) {
        continue;
      }
      const sign = metric.dataProps.sign === MetricSign.NEGATIVE ? -1 : 1;

      const metricData = metric.companyMetrics[0].metricData.find((data) =>
        data.period === period &&
        data.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
      );

      if (metricData) {
        total += sign * (Number(metricData.rowValue) || 0);
      }
    }

    const endingMetricData = endingMetric.controls.companyMetrics.controls[0].controls.metricData.controls.find((data) =>
      data.value.period === period &&
      data.value.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET),
    );


    if (endingMetricData) {
      endingMetricData.controls.rowValue.patchValue(total.toString(), { emitEvent: false });
    }
  }


  /**
   * Returns the 'rowValue' of the latest (by date) 'ENDING' metric data
   * that must precede the given date. The search is performed in the group's
   * metrics data and considers 'ACTUAL' or 'BUDGET' role metrics depending
   * on the 'isActual' flag value.
   *
   * @param group - Metric group which should contain the metric and their metric data
   * @param date - The date that appropriate metric data should precede
   * @param isActual - Whether actual or budget metrics are considered
   * @returns The 'rowValue' of found metric data or `null` if nothing is found
   */
  static getPreviousEnding(group: MetricGroup, date: LocalDate, isActual: boolean): string {
    // Filter only relevant metrics having ENDING type and relevant role
    const relevantEndingMetricData = group.metrics
      .filter((m) => m.isEnding())
      .flatMap((m) => m.companyMetrics)
      .flatMap((cm) => cm.metricData)
      .filter((md) =>
        md.period < date
        && md.rowValue
        && md.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET));

    // Sort filtered metric data in descending order and return the rowValue of the first item, if any
    return relevantEndingMetricData
      .sort((a, b) => b.period.localeCompare(a.period))
      .map((md) => md.rowValue)
      .shift() ?? null;
  }

  // does the same as the method above, but for MetricGroupForm
  static getPreviousEndingForm(group: MetricGroupForm, date: LocalDate, isActual: boolean): string {
    // Filter only relevant metrics having ENDING type and relevant role
    const relevantEndingMetricData = group.controls.metrics.controls
      .filter((m) => m.value.props.type === MetricType.ENDING)
      .flatMap((m) => m.controls.companyMetrics.controls)
      .flatMap((cm) => cm.controls.metricData.controls)
      .map((md) => md.value)
      .filter((md) =>
        md.period < date
        && md.rowValue
        && md.role === (isActual ? MetricDataRole.ACTUAL : MetricDataRole.BUDGET));

    // Sort filtered metric data in descending order and return the rowValue of the first item, if any
    return relevantEndingMetricData
      .sort((a, b) => b.period.localeCompare(a.period))
      .map((md) => md.rowValue)
      .shift() ?? null;
  }

  static setSignsForGroups(groups: MetricGroup[]): void {
    groups.forEach(this.setSignsForGroup);
  }

  static setSignsForGroup(group: MetricGroup): void {
    group.metrics.forEach(MetricCalculationService.setSignsForMetric);
  }

  static setSignsForDefaultGroup(group: DefaultMetricGroup): void {
    group.defaultMetrics.forEach(MetricCalculationService.setSignsForMetric);
  }

  static setSignsForMetric(m: Metric | DefaultMetric): void {
    if (m.dataProps.dataType === MetricDataType.DATE) {
      m.dataProps.iconNameAndStylingLeft = 'calendar.svg; icon-size-0p825';
    } else if (m.dataProps.dataType === MetricDataType.STRING) {
      m.dataProps.iconNameAndStylingLeft = 'abc.svg; icon-size-0p825';
    } else if (m.dataProps.dataType === MetricDataType.PERCENT) {
      m.dataProps.iconNameAndStylingLeft = 'percent.svg; icon-size-0p825';
    } else if (m.dataProps.dataType === MetricDataType.DOCUMENT) {
      m.dataProps.iconNameAndStylingLeft = 'documents-stack.svg; icon-size-0p825';
    } else {
      switch (m.dataProps.sign) {
        case MetricSign.ANY:
          m.dataProps.iconNameAndStylingLeft = 'plus-minus.svg; icon-size-0p825';
          break;
        case MetricSign.POSITIVE:
          m.dataProps.iconNameAndStylingLeft = 'plus.svg; icon-size-0p825';
          break;
        case MetricSign.NEGATIVE:
          m.dataProps.iconNameAndStylingLeft = 'minus.svg; icon-size-0p825';
          break;
        default:
          m.dataProps.iconNameAndStylingLeft = 'plus-minus.svg; icon-size-0p825';
      }
      if (m.props.type === MetricType.ENDING || m.props.type === MetricType.STARTING) {
        m.dataProps.iconNameAndStylingLeft = 'sum.svg; icon-size-0p825';
      }
    }
  }

  static setGroupsVisibility(groups: MetricGroup[]): void {
    groups.forEach(this.setGroupVisibility);
  }

  static setGroupVisibility(group: MetricGroup): void {
    group.temp = {
      isSomeMetricsSelected: group.metrics.some((metric) => !metric.companyMetrics[0].isHidden),
      isAllMetricsSelected: group.metrics.every((metric) => !metric.companyMetrics[0].isHidden),
    };
  }
}
